import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { useState } from "react";
import { BookOpen, Notebook, Sparkles, ArrowRight, Mail } from "lucide-react";
import { useT } from "@/lib/i18n";
import { apiFetch } from "@/lib/api";

const TILES = [
  { key: "books", Icon: BookOpen, image: "/images/cellar.jpg" },
  { key: "notebooks", Icon: Notebook, image: "/images/tasting.jpg" },
  { key: "accessories", Icon: Sparkles, image: "/images/coach.jpg" },
];

const LibraryPage = () => {
  const t = useT();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setError("");
    const { ok, error: err } = await apiFetch(`/api/email/library-waitlist`, {
      method: "POST",
      body: JSON.stringify({ email, source: "library" }),
    });
    if (!ok) {
      setError(err || "Could not subscribe. Please try again.");
      return;
    }
    setSubmitted(true);
  };

  return (
    <Layout testId="page-library">
      <SEO title={t("library.seo.title")} description={t("library.seo.desc")} />

      {/* Hero */}
      <section className="bg-[color:var(--gv-ink)] text-white py-24 md:py-32 grain relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <img src="/images/educativos.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="max-w-4xl mx-auto px-6 lg:px-10 relative">
          <div className="eyebrow text-[color:var(--gv-pink)] mb-5" data-testid="library-eyebrow">
            {t("library.eyebrow")}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6" data-testid="library-title">
            {t("library.title_a")} <span className="italic text-[color:var(--gv-pink)]">{t("library.title_b")}</span>
          </h1>
          <p className="text-base md:text-lg text-white/75 max-w-2xl leading-relaxed mb-9" data-testid="library-sub">
            {t("library.sub")}
          </p>

          {!submitted ? (
            <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md" data-testid="library-form">
              <div className="relative flex-1">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 pl-10 pr-4 py-3 rounded-[4px] focus:border-[color:var(--gv-pink)] focus:outline-none transition-colors"
                  data-testid="library-email-input"
                />
              </div>
              <button type="submit" className="btn-primary" data-testid="library-submit">
                {t("library.notify_me")} <ArrowRight size={15} />
              </button>
            </form>
          ) : (
            <div className="bg-white/10 border border-[color:var(--gv-pink)] px-5 py-4 rounded-[4px] max-w-md" data-testid="library-success">
              🍷 {t("library.success")}
            </div>
          )}
          {error && (
            <div className="mt-3 bg-rose-500/20 border border-rose-400 text-white px-4 py-2 rounded-[4px] text-sm max-w-md" data-testid="library-error">
              {error}
            </div>
          )}
        </div>
      </section>

      {/* Tiles */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <div className="eyebrow mb-3">{t("library.tiles_eyebrow")}</div>
            <h2 className="font-display text-3xl md:text-5xl text-[color:var(--gv-ink)]">{t("library.tiles_title")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TILES.map(({ key, Icon, image }) => (
              <div
                key={key}
                className="bg-white border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden hover:border-[color:var(--gv-pink)] transition-colors group"
                data-testid={`library-tile-${key}`}
              >
                <div className="aspect-[4/3] bg-[color:var(--gv-mist)] overflow-hidden">
                  <img src={image} alt={t(`library.tile.${key}.title`)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[color:var(--gv-blue)] mb-3">
                    <Icon size={13} /> {t(`library.tile.${key}.eyebrow`)}
                  </div>
                  <div className="font-display text-xl text-[color:var(--gv-ink)] mb-2">{t(`library.tile.${key}.title`)}</div>
                  <p className="text-sm text-[color:var(--gv-ink)]/70 leading-relaxed">{t(`library.tile.${key}.desc`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default LibraryPage;
